import { Link } from "@inertiajs/react"
import { X } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Badge, type BadgeTone } from "@/components/ui/badge"
import { UserLink } from "@/components/ui/user-link"
import { fmtRelativeTime } from "@/lib/format"
import type { FeedbackDrawerData } from "@/types/user_feedback"

// Right-rail drawer for a single feedback item. v1 is read-only — no status
// dropdown, no action buttons, no sentiment chip. Source links to the
// originating ticket conversation; topics + related convos render only when
// present so empty states stay quiet.

type Props = {
  data: FeedbackDrawerData | null
  onClose: () => void
}

const TONE_BY_CATEGORY: Record<string, BadgeTone> = {
  bug:            "red",
  recommendation: "gold",
  unresolved:     "warning",
  general:        "neutral",
  rating:         "success",
  bad_behavior:   "error",
}

function toneFor(category: string): BadgeTone {
  return TONE_BY_CATEGORY[category] ?? "neutral"
}

function fmtAbsolute(iso: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString()
}

export function FeedbackDetailDrawer({ data, onClose }: Props) {
  if (!data) {
    return (
      <Card className="p-6 text-sm text-app-muted">
        Select an item on the left to see details.
      </Card>
    )
  }

  return (
    <Card className="space-y-5 p-5">
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-2">
          <Badge tone={toneFor(data.category)}>{data.categoryLabel}</Badge>
          <h2 className="text-lg font-semibold leading-snug">{data.title}</h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close detail"
          className="rounded-control p-1 text-app-muted hover:bg-white/5 hover:text-app-text"
        >
          <X size={18} strokeWidth={2} />
        </button>
      </header>

      {data.description && (
        <section className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Description</div>
          <p className="text-sm leading-relaxed text-app-text">{data.description}</p>
        </section>
      )}

      {data.customerQuote && (
        <section className="space-y-1">
          <div className="text-xs uppercase tracking-wide text-app-subtle">Customer quote</div>
          <Card variant="inset" className="p-3">
            <p className="text-sm italic leading-relaxed text-app-text">
              &ldquo;{data.customerQuote}&rdquo;
            </p>
            <div className="mt-2 text-xs text-app-muted">
              —{" "}
              <UserLink
                id={data.appuserId}
                name={data.customerName}
                email={data.customerEmail}
                className="text-app-muted hover:text-brand-red"
              />
              {data.customerSubmittedAt && (
                <span> · {fmtRelativeTime(data.customerSubmittedAt)}</span>
              )}
            </div>
          </Card>
        </section>
      )}

      <section className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Source</div>
        <div className="text-sm text-app-text">{data.agentName ?? "—"}</div>
        {data.skillName && (
          <div className="text-xs text-app-muted">{data.skillName}</div>
        )}
        {data.convoId != null && (
          <Link
            href={`/portal/tickets/${data.convoId}`}
            className="inline-block text-xs text-brand-red hover:text-brand-redDeep"
          >
            Open conversation →
          </Link>
        )}
      </section>

      <section className="space-y-1">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Last reported</div>
        <div className="text-sm text-app-text">{fmtRelativeTime(data.lastReportedAt)}</div>
        <div className="text-xs text-app-muted">{fmtAbsolute(data.lastReportedAt)}</div>
      </section>

      <section className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Topics</div>
        {data.topics.length === 0 ? (
          <div className="text-xs text-app-muted">No topics tagged</div>
        ) : (
          <div className="flex flex-wrap gap-1.5">
            {data.topics.map((t) => (
              <Badge key={t.key} tone="neutral">
                {t.name}
              </Badge>
            ))}
          </div>
        )}
      </section>

      <section className="space-y-2">
        <div className="text-xs uppercase tracking-wide text-app-subtle">Related conversations</div>
        {data.relatedConvos.length === 0 ? (
          <div className="text-xs text-app-muted">No related conversations</div>
        ) : (
          <ul className="space-y-1.5">
            {data.relatedConvos.slice(0, 3).map((c) => (
              <li key={c.convoId}>
                <Link
                  href={`/portal/tickets/${c.convoId}`}
                  className="block rounded-control border border-border bg-white/5 px-3 py-2 text-xs hover:bg-white/10"
                >
                  <div className="text-app-text">{c.customerName ?? `Convo #${c.convoId}`}</div>
                  {c.quote && (
                    <div className="mt-0.5 line-clamp-2 text-app-muted">{c.quote}</div>
                  )}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </Card>
  )
}
